/**
 * Primitives Viewer
 * Interactive display of all primitive components (Text, Button, Input, etc.)
 */

import React, { useState } from "react";
// Raw Text is deliberate here: these demos render raw Chakra alongside CDS variants for comparison.
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Box, SimpleGrid, Card, CardBody, Badge, Code, IconButton, Text, Skeleton } from "@chakra-ui/react";
import { ViewIcon, CopyIcon } from "@chakra-ui/icons";

import CDS, { useResolveColor } from "design-system";

interface ComponentExampleProps {
  title: string;
  description: string;
  codeExample: string;
  children: React.ReactNode;
  props?: Record<string, any>;
}

function ComponentExample({ title, description, codeExample, children, props = {} }: ComponentExampleProps) {
  const [showCode, setShowCode] = useState(false);
  const exampleBg = useResolveColor(CDS.Color.surface.secondary);
  const borderColor = useResolveColor(CDS.Color.border.secondary);

  return (
    <Card>
      <CardBody>
        <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
          <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between}>
            <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.tiny}>
              <CDS.Text14 fontWeight="semibold">{title}</CDS.Text14>
              <CDS.Text12 color="gray.600">
                {description}
              </CDS.Text12>
            </CDS.Stack>
            <CDS.Stack direction={CDS.Stack.direction.horizontal}>
              <IconButton
                aria-label="Toggle code"
                icon={<ViewIcon />}
                size="sm"
                variant="ghost"
                onClick={() => setShowCode(!showCode)}
              />
              <IconButton
                aria-label="Copy code"
                icon={<CopyIcon />}
                size="sm"
                variant="ghost"
                onClick={() => navigator.clipboard.writeText(codeExample)}
              />
            </CDS.Stack>
          </CDS.Stack>

          {/* Example Display */}
          <Box
            p={6}
            bg={exampleBg}
            borderRadius="md"
            border="1px solid"
            borderColor={borderColor}
            minH="60px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            {children}
          </Box>

          {/* Props Display */}
          {Object.keys(props).length > 0 && (
            <Box>
              <CDS.Text12 fontWeight="medium" mb={2}>
                Props:
              </CDS.Text12>
              <SimpleGrid columns={2} spacing={CDS.Spacing.small}>
                {Object.entries(props).map(([key, value]) => (
                  <CDS.Stack direction={CDS.Stack.direction.horizontal} key={key} justify={CDS.Stack.justify.between}>
                    <Code fontSize="xs">{key}</Code>
                    <Badge variant="outline" fontSize="xs">
                      {typeof value === "string" ? `"${value}"` : String(value)}
                    </Badge>
                  </CDS.Stack>
                ))}
              </SimpleGrid>
            </Box>
          )}

          {/* Code Display */}
          {showCode && (
            <Box>
              <CDS.Text12 fontWeight="medium" mb={2}>
                Code:
              </CDS.Text12>
              <Code
                display="block"
                p={3}
                whiteSpace="pre-wrap"
                fontSize="sm"
                bg="gray.800"
                color="gray.100"
                borderRadius="md"
              >
                {codeExample}
              </Code>
            </Box>
          )}
        </CDS.Stack>
      </CardBody>
    </Card>
  );
}

function ButtonExamples() {
  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall}>Button Component</CDS.Text>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <ComponentExample
          title="Button Intents"
          description="Different button intents for various actions"
          codeExample={`<CDS.Button intent={CDS.buttonIntent.primary()}>
  Primary Action
</CDS.Button>

<CDS.Button intent={CDS.buttonIntent.secondary()}>
  Secondary Action
</CDS.Button>

<CDS.Button intent={CDS.buttonIntent.success()}>
  Success Action
</CDS.Button>`}
          props={{ intent: "primary", size: "medium", variant: "solid" }}
        >
          <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
            <CDS.Button intent={CDS.Button.intent.primary}>Primary</CDS.Button>
            <CDS.Button intent={CDS.Button.intent.secondary}>Secondary</CDS.Button>
            <CDS.Button intent={CDS.Button.intent.success}>Success</CDS.Button>
            <CDS.Button intent={CDS.Button.intent.error}>Error</CDS.Button>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Button Sizes"
          description="Different button sizes for various contexts"
          codeExample={`<CDS.Button size={CDS.buttonSize.small()}>
  Small Button
</CDS.Button>

<CDS.Button size={CDS.buttonSize.medium()}>
  Medium Button
</CDS.Button>

<CDS.Button size={CDS.buttonSize.large()}>
  Large Button
</CDS.Button>`}
          props={{ size: "medium", intent: "primary", variant: "solid" }}
        >
          <CDS.Stack spacing={CDS.Spacing.small}>
            <CDS.Button size={CDS.Button.size.small}>Small</CDS.Button>
            <CDS.Button size={CDS.Button.size.medium}>Medium</CDS.Button>
            <CDS.Button size={CDS.Button.size.large}>Large</CDS.Button>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Button Variants"
          description="Different visual styles for buttons"
          codeExample={`<CDS.Button variant={CDS.buttonVariant.soli1()}>
  Solid Button
</CDS.Button>

<CDS.Button variant={CDS.buttonVariant.outline()}>
  Outline Button
</CDS.Button>

<CDS.Button variant={CDS.buttonVariant.ghost()}>
  Ghost Button
</CDS.Button>`}
          props={{ variant: "solid", intent: "primary", size: "medium" }}
        >
          <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
            <CDS.Button variant={CDS.Button.variant.solid}>Solid</CDS.Button>
            <CDS.Button variant={CDS.Button.variant.outline}>Outline</CDS.Button>
            <CDS.Button variant={CDS.Button.variant.ghost}>Ghost</CDS.Button>
            <CDS.Button variant={CDS.Button.variant.link}>Link</CDS.Button>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Button States"
          description="Loading and disabled states"
          codeExample={`<CDS.Button loading={true}>
  Loading Button
</CDS.Button>

<CDS.Button disabled={true}>
  Disabled Button
</CDS.Button>`}
          props={{ loading: true, disabled: false }}
        >
          <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
            <CDS.Button loading>Loading</CDS.Button>
            <CDS.Button disabled>Disabled</CDS.Button>
          </CDS.Stack>
        </ComponentExample>
      </SimpleGrid>
    </CDS.Stack>
  );
}

function TextExamples() {
  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall}>Text & Span Components</CDS.Text>

      <SimpleGrid columns={1} spacing={6}>
        <ComponentExample
          title="Typography Hierarchy"
          description="Different text variants for content hierarchy"
          codeExample={`<CDS.Text variant="display-large">
  Display Large Text
</CDS.Text>

<CDS.Text variant="headline-medium">
  Headline Medium Text
</CDS.Text>

<CDS.Text variant="body-medium">
  Body medium text for regular content
</CDS.Text>

<CDS.Text variant="caption">
  Caption text for additional information
</CDS.Text>`}
          props={{ variant: "body-medium" }}
        >
          <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.small} w="full">
            <Text fontSize="3xl" fontWeight="bold">
              Display Large Text
            </Text>
            <Text fontSize="xl" fontWeight="semibold">
              Headline Medium Text
            </Text>
            <Text fontSize="lg" fontWeight="medium">
              Title Large Text
            </Text>
            <Text fontSize="md">Body medium text for regular content</Text>
            <Text fontSize="sm" color="gray.600">
              Caption text for additional information
            </Text>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Text Colors"
          description="Semantic text colors from the design system"
          codeExample={`<CDS.Text color={CDS.Color.text.primary}>
  Primary text color
</CDS.Text>

<CDS.Text color={CDS.Color.text.secondary}>
  Secondary text color
</CDS.Text>

<CDS.Text color={CDS.Color.text.success}>
  Success text color
</CDS.Text>`}
          props={{ color: "text.primary" }}
        >
          <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.small} w="full">
            <CDS.Text color={CDS.Color.text.primary}>Primary text color</CDS.Text>
            <CDS.Text color={CDS.Color.text.secondary}>Secondary text color</CDS.Text>
            <CDS.Text color={CDS.Color.text.tertiary}>Tertiary text color</CDS.Text>
            <CDS.Text color={CDS.Color.text.link}>Link text color</CDS.Text>
            <CDS.Text color={CDS.Color.text.success}>Success text color</CDS.Text>
            <CDS.Text color={CDS.Color.text.error}>Error text color</CDS.Text>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Span Component"
          description="Inline text component with styling support"
          codeExample={`<CDS.Span color={CDS.Color.text.primary}>
  Basic inline text
</CDS.Span>

<CDS.Span
  color={CDS.Color.text.secondary}
  bg={CDS.Color.surface.secondary}
  p={CDS.Spacing.tiny}
  borderRadius={CDS.Radius.tag}
>
  Styled span
</CDS.Span>

<CDS.Span
  fontWeight="bold"
  color={CDS.Color.text.success}
  textTransform="uppercase"
>
  Emphasis span
</CDS.Span>`}
          props={{ display: "inline", color: "text.primary" }}
        >
          <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.small} w="full">
            <CDS.Text>
              This is a paragraph with{" "}
              <CDS.Span color={CDS.Color.text.primary} fontWeight="semibold">
                inline text
              </CDS.Span>{" "}
              and{" "}
              <CDS.Span
                color={CDS.Color.text.secondary}
                bg={CDS.Color.surface.secondary}
                p={CDS.Spacing.tiny}
                borderRadius={CDS.Radius.tag}
              >
                styled spans
              </CDS.Span>
              .
            </CDS.Text>

            <CDS.Text>
              You can also use spans for{" "}
              <CDS.Span fontWeight="bold" color={CDS.Color.text.success} textTransform="uppercase">
                emphasis
              </CDS.Span>{" "}
              and{" "}
              <CDS.Span color={CDS.Color.text.link} textDecoration="underline" cursor="pointer">
                interactive elements
              </CDS.Span>
              .
            </CDS.Text>

            <CDS.Text>
              Mix different{" "}
              <CDS.Span
                bg={CDS.Color.primitive.red(100)}
                color={CDS.Color.primitive.red(800)}
                p={CDS.Spacing.tiny}
                borderRadius={CDS.Radius.tiny}
              >
                background colors
              </CDS.Span>{" "}
              and{" "}
              <CDS.Span
                bg={CDS.Color.primitive.blue(100)}
                color={CDS.Color.primitive.blue(800)}
                p={CDS.Spacing.tiny}
                borderRadius={CDS.Radius.tiny}
              >
                styles
              </CDS.Span>{" "}
              together.
            </CDS.Text>
          </CDS.Stack>
        </ComponentExample>
      </SimpleGrid>
    </CDS.Stack>
  );
}

function InputExamples() {
  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall}>Input Component</CDS.Text>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <ComponentExample
          title="Input Sizes"
          description="Small, medium, and large input sizes"
          codeExample={`<CDS.Input size={CDS.Input.size.small} placeholder="Small" />
<CDS.Input size={CDS.Input.size.medium} placeholder="Medium" />
<CDS.Input size={CDS.Input.size.large} placeholder="Large" />`}
          props={{ size: "medium", placeholder: "string" }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} w="full">
            <CDS.Input size={CDS.Input.size.small} placeholder="Small input" />
            <CDS.Input size={CDS.Input.size.medium} placeholder="Medium input" />
            <CDS.Input size={CDS.Input.size.large} placeholder="Large input" />
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Validation States"
          description="Default, success, warning, error, and disabled"
          codeExample={`<CDS.Input placeholder="Default" />
<CDS.Input state={CDS.Input.state.success} placeholder="Success" />
<CDS.Input state={CDS.Input.state.warning} placeholder="Warning" />
<CDS.Input
  state={CDS.Input.state.error}
  errorText="This field is required"
  placeholder="Error"
/>
<CDS.Input disabled placeholder="Disabled" />`}
          props={{ state: "default" }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} w="full">
            <CDS.Input placeholder="Default" />
            <CDS.Input state={CDS.Input.state.success} placeholder="Success" />
            <CDS.Input state={CDS.Input.state.warning} placeholder="Warning" />
            <CDS.Input state={CDS.Input.state.error} errorText="This field is required" placeholder="Error" />
            <CDS.Input disabled placeholder="Disabled" />
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="With Label & Helper Text"
          description="Inputs with labels, helper text, and error messages"
          codeExample={`<CDS.Input
  label="Email Address"
  helperText="We'll never share your email"
  placeholder="you@example.com"
  type="email"
  fullWidth
/>
<CDS.Input
  label="Password"
  state={CDS.Input.state.error}
  errorText="Must be at least 8 characters"
  type="password"
  fullWidth
/>`}
          props={{ label: "string", helperText: "string", fullWidth: true }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} w="full">
            <CDS.Input
              label="Email Address"
              helperText="We'll never share your email"
              placeholder="you@example.com"
              type="email"
              fullWidth
            />
            <CDS.Input
              label="Password"
              state={CDS.Input.state.error}
              errorText="Must be at least 8 characters"
              placeholder="Enter password"
              type="password"
              fullWidth
            />
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Textarea"
          description="Multi-line text input with resize and validation"
          codeExample={`<CDS.Textarea
  label="Description"
  helperText="Briefly describe the topic"
  placeholder="Enter description"
  rows={3}
  fullWidth
/>
<CDS.Textarea
  state={CDS.Textarea.state.error}
  errorText="Description is required"
  placeholder="Invalid textarea"
  rows={2}
  fullWidth
/>`}
          props={{ rows: 4, resize: "vertical" }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} w="full">
            <CDS.Textarea
              label="Description"
              helperText="Briefly describe the topic"
              placeholder="Enter description"
              rows={3}
              fullWidth
            />
            <CDS.Textarea
              state={CDS.Textarea.state.error}
              errorText="Description is required"
              placeholder="Invalid textarea"
              rows={2}
              fullWidth
            />
          </CDS.Stack>
        </ComponentExample>
      </SimpleGrid>
    </CDS.Stack>
  );
}

function IconExamples() {
  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall}>Icon Component</CDS.Text>

      <ComponentExample
        title="Icon Sizes"
        description="Different icon sizes with consistent scaling"
        codeExample={`<CDS.Icon
  name="star"
  size={CDS.iconSize.small()}
/>

<CDS.Icon
  name="star"
  size={CDS.iconSize.medium()}
/>

<CDS.Icon
  name="star"
  size={CDS.iconSize.large()}
/>`}
        props={{ name: "star", size: "medium" }}
      >
        <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.medium}>
          <ViewIcon boxSize={4} />
          <ViewIcon boxSize={6} />
          <ViewIcon boxSize={8} />
          <ViewIcon boxSize={10} />
        </CDS.Stack>
      </ComponentExample>
    </CDS.Stack>
  );
}

function DividerExamples() {
  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall}>Divider Component</CDS.Text>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <ComponentExample
          title="Size Variants"
          description="Different divider thicknesses for visual hierarchy"
          codeExample={`<CDS.Divider size={CDS.Divider.size.thin} />
<CDS.Divider size={CDS.Divider.size.medium} />
<CDS.Divider size={CDS.Divider.size.thick} />`}
          props={{ size: "thin", variant: "solid", color: "border.secondary" }}
        >
          <CDS.Stack spacing={CDS.Spacing.medium} align={CDS.Stack.align.stretch} w="full">
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Thin (1px, default)
              </CDS.Text10>
              <CDS.Divider size={CDS.Divider.size.thin} />
            </Box>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Medium (1.5px)
              </CDS.Text10>
              <CDS.Divider size={CDS.Divider.size.medium} />
            </Box>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Thick (2px)
              </CDS.Text10>
              <CDS.Divider size={CDS.Divider.size.thick} />
            </Box>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Style Variants"
          description="Different line styles for visual variety"
          codeExample={`<CDS.Divider variant={CDS.Divider.variant.solid} />
<CDS.Divider variant={CDS.Divider.variant.dashed} />
<CDS.Divider variant={CDS.Divider.variant.dotted} />`}
          props={{ variant: "solid", size: "thin", color: "border.secondary" }}
        >
          <CDS.Stack spacing={CDS.Spacing.medium} align={CDS.Stack.align.stretch} w="full">
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Solid (default)
              </CDS.Text10>
              <CDS.Divider variant={CDS.Divider.variant.solid} />
            </Box>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Dashed
              </CDS.Text10>
              <CDS.Divider variant={CDS.Divider.variant.dashed} />
            </Box>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Dotted
              </CDS.Text10>
              <CDS.Divider variant={CDS.Divider.variant.dotted} />
            </Box>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Color Options"
          description="Semantic border colors from the design system"
          codeExample={`<CDS.Divider color={CDS.Color.border.primary} />
<CDS.Divider color={CDS.Color.border.secondary} />
<CDS.Divider color={CDS.Color.border.tertiary} />`}
          props={{ color: "border.secondary", size: "thin", variant: "solid" }}
        >
          <CDS.Stack spacing={CDS.Spacing.medium} align={CDS.Stack.align.stretch} w="full">
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Primary Border
              </CDS.Text10>
              <CDS.Divider color={CDS.Color.border.primary} />
            </Box>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Secondary Border (default)
              </CDS.Text10>
              <CDS.Divider color={CDS.Color.border.secondary} />
            </Box>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Tertiary Border
              </CDS.Text10>
              <CDS.Divider color={CDS.Color.border.tertiary} />
            </Box>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Vertical Orientation"
          description="Vertical dividers for horizontal layouts"
          codeExample={`<CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.medium}>
  <Text>Left Content</Text>
  <CDS.Divider orientation={CDS.Divider.orientation.vertical} height="40px" />
  <Text>Right Content</Text>
</CDS.Stack>`}
          props={{ orientation: "vertical", height: "40px" }}
        >
          <CDS.Stack
            direction={CDS.Stack.direction.horizontal}
            spacing={CDS.Spacing.medium}
            align={CDS.Stack.align.center}
          >
            <Text fontSize="sm">Left Content</Text>
            <CDS.Divider orientation={CDS.Divider.orientation.vertical} height="40px" />
            <Text fontSize="sm">Right Content</Text>
          </CDS.Stack>
        </ComponentExample>
      </SimpleGrid>
    </CDS.Stack>
  );
}

function TagExamples() {
  const [exampleTags, setExampleTags] = useState([
    { id: 1, title: "John Doe", variant: CDS.Tag.variant.neutral },
    { id: 2, title: "Jane Smith", variant: CDS.Tag.variant.primary },
    { id: 3, title: "Bob Wilson", variant: CDS.Tag.variant.success },
  ]);

  const removeTag = (id: number) => {
    setExampleTags((tags) => tags.filter((tag) => tag.id !== id));
  };

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall}>Tag Components</CDS.Text>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <ComponentExample
          title="Base Tag Variants"
          description="Semantic tag variants with consistent Camino brand colors"
          codeExample={`<CDS.Tag>Default</CDS.Tag>
<CDS.Tag variant={CDS.Tag.variant.primary}>
  Primary
</CDS.Tag>
<CDS.Tag variant={CDS.Tag.variant.success}>
  Success
</CDS.Tag>
<CDS.Tag variant={CDS.Tag.variant.warning}>
  Warning
</CDS.Tag>
<CDS.Tag variant={CDS.Tag.variant.error}>
  Error
</CDS.Tag>`}
          props={{ variant: "neutral", size: "medium" }}
        >
          <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small} wrap="wrap">
            <CDS.Tag>Default</CDS.Tag>
            <CDS.Tag variant={CDS.Tag.variant.primary}>Primary</CDS.Tag>
            <CDS.Tag variant={CDS.Tag.variant.success}>Success</CDS.Tag>
            <CDS.Tag variant={CDS.Tag.variant.warning}>Warning</CDS.Tag>
            <CDS.Tag variant={CDS.Tag.variant.error}>Error</CDS.Tag>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Tag Sizes"
          description="Different tag sizes for various contexts"
          codeExample={`<CDS.Tag size={CDS.Tag.size.small}>
  Small
</CDS.Tag>
<CDS.Tag size={CDS.Tag.size.medium}>
  Medium
</CDS.Tag>
<CDS.Tag size={CDS.Tag.size.large}>
  Large
</CDS.Tag>`}
          props={{ size: "medium", variant: "primary" }}
        >
          <CDS.Stack
            direction={CDS.Stack.direction.horizontal}
            spacing={CDS.Spacing.small}
            align={CDS.Stack.align.center}
          >
            <CDS.Tag size={CDS.Tag.size.small} variant={CDS.Tag.variant.primary}>
              Small
            </CDS.Tag>
            <CDS.Tag size={CDS.Tag.size.medium} variant={CDS.Tag.variant.primary}>
              Medium
            </CDS.Tag>
            <CDS.Tag size={CDS.Tag.size.large} variant={CDS.Tag.variant.primary}>
              Large
            </CDS.Tag>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Closable Tags"
          description="Tags with close functionality for dynamic lists"
          codeExample={`<CDS.Tag
  variant={CDS.Tag.variant.primary}
  closable
  onClose={() => removeTag(tag.id)}
>
  Closable Tag
</CDS.Tag>`}
          props={{ closable: true, onClose: "function" }}
        >
          <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small} wrap="wrap">
            {exampleTags.map((tag) => (
              <CDS.Tag key={tag.id} variant={tag.variant} closable onClose={() => removeTag(tag.id)}>
                {tag.title}
              </CDS.Tag>
            ))}
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="NameTag Variants"
          description="Specialized tags for names with interactive actions"
          codeExample={`<CDS.NameTag title="John Doe" />

<CDS.NameTag
  title="Jane Smith"
  variant={CDS.Tag.variant.primary}
  onClick={() => handleClick()}
/>

<CDS.NameTag
  title="Bob Wilson"
  variant={CDS.Tag.variant.success}
  onEdit={() => handleEdit()}
  onDelete={() => handleDelete()}
/>`}
          props={{ title: "string", variant: "neutral", onClick: "optional" }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} align={CDS.Stack.align.stretch}>
            <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small} wrap="wrap">
              <CDS.NameTag title="John Doe" />
              <CDS.NameTag
                title="Jane Smith"
                variant={CDS.Tag.variant.primary}
                onClick={() => console.log("Name clicked")}
              />
              <CDS.NameTag
                title="Bob Wilson"
                variant={CDS.Tag.variant.success}
                onEdit={() => console.log("Edit clicked")}
                onDelete={() => console.log("Delete clicked")}
              />
            </CDS.Stack>
          </CDS.Stack>
        </ComponentExample>
      </SimpleGrid>
    </CDS.Stack>
  );
}

function FormControlExamples() {
  const [radioValue, setRadioValue] = useState("basic");
  const [toggleOn, setToggleOn] = useState(true);

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall}>Form Control Components</CDS.Text>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <ComponentExample
          title="Checkbox"
          description="Individual checkboxes with validation states"
          codeExample={`<CDS.Checkbox label="I agree to terms" />
<CDS.Checkbox label="Subscribe to newsletter" defaultChecked />
<CDS.Checkbox isDisabled label="Disabled" />
<CDS.Checkbox
  state={CDS.Checkbox.state.error}
  errorText="This field is required"
  label="Invalid checkbox"
/>`}
          props={{ label: "string", state: "default" }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} align={CDS.Stack.align.start}>
            <CDS.Checkbox label="I agree to terms" />
            <CDS.Checkbox label="Subscribe to newsletter" defaultChecked />
            <CDS.Checkbox isDisabled label="Disabled checkbox" />
            <CDS.Checkbox
              state={CDS.Checkbox.state.error}
              errorText="This field is required"
              label="Invalid checkbox"
            />
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Radio Group"
          description="Radio button groups with validation"
          codeExample={`<CDS.Radio.Group
  name="plan"
  label="Choose a plan"
  value={radioValue}
  onChange={setRadioValue}
  options={[
    { value: "basic", label: "Basic Plan" },
    { value: "pro", label: "Pro Plan" },
    { value: "enterprise", label: "Enterprise" },
  ]}
/>`}
          props={{ name: "string", options: "array", direction: "vertical" }}
        >
          <CDS.Radio.Group
            name="plan"
            label="Choose a plan"
            value={radioValue}
            onChange={setRadioValue}
            options={[
              { value: "basic", label: "Basic Plan" },
              { value: "pro", label: "Pro Plan" },
              { value: "enterprise", label: "Enterprise" },
            ]}
          />
        </ComponentExample>

        <ComponentExample
          title="Toggle Switch"
          description="Toggle switches for boolean settings"
          codeExample={`<CDS.Toggle
  label="Enable notifications"
  isChecked={toggleOn}
  onChange={(e) => setToggleOn(e.target.checked)}
/>
<CDS.Toggle label="Dark mode" isDisabled />`}
          props={{ label: "string", isChecked: true }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} align={CDS.Stack.align.start}>
            <CDS.Toggle
              label="Enable notifications"
              isChecked={toggleOn}
              onChange={(e) => setToggleOn(e.target.checked)}
            />
            <CDS.Toggle label="Dark mode" isDisabled />
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Select Dropdown"
          description="Dropdown select with validation states"
          codeExample={`<CDS.Select
  label="Country"
  placeholder="Select country"
  fullWidth
  options={[
    { value: "us", label: "United States" },
    { value: "ca", label: "Canada" },
    { value: "uk", label: "United Kingdom" },
  ]}
/>
<CDS.Select
  label="Plan (invalid)"
  state={CDS.Select.state.error}
  errorText="Please select a plan"
  placeholder="Select plan"
  fullWidth
  options={[...]}
/>`}
          props={{ label: "string", options: "array", placeholder: "string" }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} w="full">
            <CDS.Select
              label="Country"
              placeholder="Select country"
              fullWidth
              options={[
                { value: "us", label: "United States" },
                { value: "ca", label: "Canada" },
                { value: "uk", label: "United Kingdom" },
              ]}
            />
            <CDS.Select
              label="Plan (invalid)"
              state={CDS.Select.state.error}
              errorText="Please select a plan"
              placeholder="Select plan"
              fullWidth
              options={[
                { value: "basic", label: "Basic" },
                { value: "pro", label: "Pro" },
              ]}
            />
          </CDS.Stack>
        </ComponentExample>
      </SimpleGrid>
    </CDS.Stack>
  );
}

function ImageExamples() {
  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall}>Image Component</CDS.Text>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <ComponentExample
          title="Aspect Ratios"
          description="Different aspect ratios for various use cases"
          codeExample={`<CDS.Image
  src="/image.jpg"
  alt="Square image"
  aspectRatio={CDS.Image.aspect.square}
/>

<CDS.Image
  src="/image.jpg"
  alt="Video aspect"
  aspectRatio={CDS.Image.aspect.video}
/>

<CDS.Image
  src="/image.jpg"
  alt="Photo aspect"
  aspectRatio={CDS.Image.aspect.photo}
/>`}
          props={{ aspectRatio: "square", fit: "cover", radius: "none" }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} w="full">
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Square (1:1)
              </CDS.Text10>
              <Box
                w="100px"
                h="100px"
                bg="gray.200"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CDS.Text10 color="gray.600">
                  1:1
                </CDS.Text10>
              </Box>
            </Box>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Video (16:9)
              </CDS.Text10>
              <Box
                w="160px"
                h="90px"
                bg="gray.200"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CDS.Text10 color="gray.600">
                  16:9
                </CDS.Text10>
              </Box>
            </Box>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Object Fit Options"
          description="How images fit within their containers"
          codeExample={`<CDS.Image
  src="/image.jpg"
  alt="Cover fit"
  fit={CDS.Image.fit.cover}
/>

<CDS.Image
  src="/image.jpg"
  alt="Contain fit"
  fit={CDS.Image.fit.contain}
/>`}
          props={{ fit: "cover", aspectRatio: "square" }}
        >
          <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Cover
              </CDS.Text10>
              <Box
                w="60px"
                h="60px"
                bg="gray.200"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CDS.Text10 color="gray.600">
                  Cover
                </CDS.Text10>
              </Box>
            </Box>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Contain
              </CDS.Text10>
              <Box
                w="60px"
                h="60px"
                bg="gray.200"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CDS.Text10 color="gray.600">
                  Contain
                </CDS.Text10>
              </Box>
            </Box>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Border Radius"
          description="Different border radius options"
          codeExample={`<CDS.Image
  src="/image.jpg"
  alt="Rounded image"
  radius={CDS.Image.radius.medium}
/>

<CDS.Image
  src="/image.jpg"
  alt="Circular image"
  radius={CDS.Image.radius.full}
/>`}
          props={{ radius: "medium", aspectRatio: "square" }}
        >
          <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Medium
              </CDS.Text10>
              <Box
                w="60px"
                h="60px"
                bg="gray.200"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CDS.Text10 color="gray.600">
                  Medium
                </CDS.Text10>
              </Box>
            </Box>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Full (Circle)
              </CDS.Text10>
              <Box
                w="60px"
                h="60px"
                bg="gray.200"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CDS.Text10 color="gray.600">
                  Full
                </CDS.Text10>
              </Box>
            </Box>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Loading States"
          description="Skeleton loading and error fallbacks"
          codeExample={`<CDS.Image
  src="/image.jpg"
  alt="Loading image"
  showSkeleton={true}
  fallback={<Text>Failed to load</Text>}
/>`}
          props={{ showSkeleton: true, loading: "lazy" }}
        >
          <CDS.Stack spacing={CDS.Spacing.small}>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Loading Skeleton
              </CDS.Text10>
              <Skeleton w="100px" h="60px" borderRadius="md" />
            </Box>
            <Box>
              <CDS.Text10 color="gray.600" mb={2}>
                Error Fallback
              </CDS.Text10>
              <Box
                w="100px"
                h="60px"
                bg="gray.100"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CDS.Text10 color="gray.500">
                  Failed to load
                </CDS.Text10>
              </Box>
            </Box>
          </CDS.Stack>
        </ComponentExample>
      </SimpleGrid>
    </CDS.Stack>
  );
}

function LinkExamples() {
  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall}>Link Component</CDS.Text>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <ComponentExample
          title="Link Variants"
          description="Different link styles for various contexts"
          codeExample={`<CDS.Link href="#" variant={CDS.Link.variant.default}>
  Default Link
</CDS.Link>

<CDS.Link href="#" variant={CDS.Link.variant.primary}>
  Primary Link
</CDS.Link>

<CDS.Link href="#" variant={CDS.Link.variant.secondary}>
  Secondary Link
</CDS.Link>

<CDS.Link href="#" variant={CDS.Link.variant.danger}>
  Danger Link
</CDS.Link>`}
          props={{ variant: "default", href: "string" }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} align={CDS.Stack.align.start}>
            <Text as="a" href="#" color="blue.500" textDecoration="underline">
              Default Link
            </Text>
            <Text as="a" href="#" color="blue.600" fontWeight="semibold" textDecoration="underline">
              Primary Link
            </Text>
            <Text as="a" href="#" color="gray.600" textDecoration="underline">
              Secondary Link
            </Text>
            <Text as="a" href="#" color="red.500" textDecoration="underline">
              Danger Link
            </Text>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="External Links"
          description="Links that open in new tabs with security"
          codeExample={`<CDS.Link
  href="https://example.com"
  external={true}
>
  External Link
</CDS.Link>

<CDS.Link
  href="https://example.com"
  external={true}
  showIcon={true}
>
  External Link with Icon
</CDS.Link>`}
          props={{ external: true, showIcon: false }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} align={CDS.Stack.align.start}>
            <Text
              as="a"
              href="https://example.com"
              target="_blank"
              rel="noopener noreferrer"
              color="blue.500"
              textDecoration="underline"
            >
              External Link
            </Text>
            <CDS.Stack
              direction={CDS.Stack.direction.horizontal}
              spacing={CDS.Spacing.tiny}
              align={CDS.Stack.align.center}
            >
              <Text
                as="a"
                href="https://example.com"
                target="_blank"
                rel="noopener noreferrer"
                color="blue.500"
                textDecoration="underline"
              >
                External Link with Icon
              </Text>
              <Box as="span" color="blue.500" fontSize="xs">
                ↗
              </Box>
            </CDS.Stack>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Navigation Links"
          description="Links for React Router navigation"
          codeExample={`<CDS.Link to="/dashboard" as={ReactRouterLink}>
  Dashboard
</CDS.Link>

<CDS.Link
  to="/settings"
  as={ReactRouterLink}
  variant={CDS.Link.variant.primary}
>
  Settings
</CDS.Link>`}
          props={{ to: "string", as: "ReactRouterLink" }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} align={CDS.Stack.align.start}>
            <Text as="a" href="#" color="blue.500" textDecoration="underline">
              Dashboard
            </Text>
            <Text as="a" href="#" color="blue.600" fontWeight="semibold" textDecoration="underline">
              Settings
            </Text>
          </CDS.Stack>
        </ComponentExample>

        <ComponentExample
          title="Link States"
          description="Disabled and loading states"
          codeExample={`<CDS.Link href="#" disabled={true}>
  Disabled Link
</CDS.Link>

<CDS.Link href="#" loading={true}>
  Loading Link
</CDS.Link>`}
          props={{ disabled: false, loading: false }}
        >
          <CDS.Stack spacing={CDS.Spacing.small} align={CDS.Stack.align.start}>
            <Text as="span" color="gray.400" cursor="not-allowed">
              Disabled Link
            </Text>
            <CDS.Stack
              direction={CDS.Stack.direction.horizontal}
              spacing={CDS.Spacing.tiny}
              align={CDS.Stack.align.center}
            >
              <Box w="3" h="3" bg="blue.500" borderRadius="full" animation="pulse 2s infinite" />
              <Text color="blue.500">Loading Link</Text>
            </CDS.Stack>
          </CDS.Stack>
        </ComponentExample>
      </SimpleGrid>
    </CDS.Stack>
  );
}

export function PrimitivesViewer() {
  const tabItems = [
    { label: "Button", content: <ButtonExamples /> },
    { label: "Text", content: <TextExamples /> },
    { label: "Input", content: <InputExamples /> },
    { label: "Form Controls", content: <FormControlExamples /> },
    { label: "Icon", content: <IconExamples /> },
    { label: "Image", content: <ImageExamples /> },
    { label: "Link", content: <LinkExamples /> },
    { label: "Divider", content: <DividerExamples /> },
    { label: "Tags", content: <TagExamples /> },
  ];

  return <CDS.Tabs items={tabItems} variant={CDS.Tabs.variant.line} size={CDS.Tabs.size.medium} defaultIndex={0} />;
}
